import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calculator, MapPin, Loader2, ArrowRight, Check } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const VEHICLES = [
  { key: "passat", label: "Passat (Standard)", blurb: "1-2 small/medium pets", upcharge: 0 },
  { key: "eclass", label: "Mercedes E-Class (Premium)", blurb: "Smoother, quieter ride", upcharge: 100 },
  { key: "vclass", label: "Mercedes V-Class (Spacious)", blurb: "Multiple pets or large breeds", upcharge: 200 },
];

// Internal price engine — not exposed to the customer.
function priceFor(miles, vehicle, crossesChannel = false) {
  if (!miles || miles <= 0) return 0;
  let base = miles <= 500 ? miles * 2.8 : 500 * 2.8 + (miles - 500) * 1.7;
  if (crossesChannel && base < 240) base = 240;
  const up = VEHICLES.find((v) => v.key === vehicle)?.upcharge || 0;
  return Math.round((base + up) * 100) / 100;
}

export default function QuoteForm({ compact = false }) {
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(null); // { miles, km, crosses_channel, pickup_label, dropoff_label }

  const prices = useMemo(() => {
    if (!route) return null;
    return VEHICLES.map((v) => ({
      ...v,
      total: priceFor(route.miles, v.key, route.crosses_channel),
    }));
  }, [route]);

  // Fire lead-notification email to admin exactly once per unique route result shown
  const notifiedKeyRef = useRef("");
  useEffect(() => {
    if (!route || !prices) return;
    const key = `${pickup}||${dropoff}||${route.miles}`;
    if (notifiedKeyRef.current === key) return;
    notifiedKeyRef.current = key;
    const [p, e, v] = prices.map((x) => x.total);
    api.post("/quotes/lead", {
      pickup_location: pickup,
      dropoff_location: dropoff,
      distance_miles: route.miles,
      price_passat: p,
      price_eclass: e,
      price_vclass: v,
      crosses_channel: !!route.crosses_channel,
    }).catch(() => { /* non-blocking, silent */ });
  }, [route, prices, pickup, dropoff]);

  const calculate = async (e) => {
    e?.preventDefault?.();
    if (!pickup.trim() || !dropoff.trim()) {
      toast.error("Enter both pickup and drop-off locations");
      return;
    }
    setLoading(true);
    let lastErr = null;
    // Up to 3 attempts (handles Cloudflare 520 / cold-start transient errors)
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const { data } = await api.post("/geocode/distance", {
          pickup_address: pickup,
          dropoff_address: dropoff,
        });
        setRoute({
          miles: data.distance_miles,
          km: data.distance_km,
          crosses_channel: !!data.crosses_channel,
          pickup_label: data.pickup?.display_name?.split(",").slice(0, 2).join(",") || pickup,
          dropoff_label: data.dropoff?.display_name?.split(",").slice(0, 2).join(",") || dropoff,
        });
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        // On 4xx (user error / not found) stop retrying
        if (status && status >= 400 && status < 500) break;
        // On 5xx or network error, wait and retry
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1200 * attempt));
      }
    }
    setLoading(false);
    const status = lastErr?.response?.status;
    if (!status || status >= 500) {
      toast.error("Our server is warming up — please tap the button once more in a few seconds.");
    } else if (status === 404 || status === 400) {
      toast.error("Could not find that route. Please enter a more specific address (city, country).");
    } else {
      toast.error(formatApiError(lastErr?.response?.data?.detail) || "Something went wrong — please try again.");
    }
  };

  const reset = () => {
    setPickup("");
    setDropoff("");
    setRoute(null);
  };

  const bookingUrl = (vehicleKey) => {
    const params = new URLSearchParams({
      pickup,
      dropoff,
      miles: String(route.miles),
      vehicle: vehicleKey,
    });
    if (route.crosses_channel) params.set("channel", "1");
    return `/book?${params.toString()}`;
  };

  // Results screen — instant prices
  if (route && prices) {
    return (
      <div className="space-y-6" data-testid="quote-results">
        <div className="rounded-md bg-primary/5 border border-primary/30 p-5 flex items-start gap-3">
          <span className="inline-flex h-9 w-9 rounded-full bg-primary text-primary-foreground items-center justify-center shrink-0">
            <Check size={18} />
          </span>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">Your route</p>
            <p className="font-display text-xl leading-snug mt-1">
              {route.pickup_label} <span className="text-muted-foreground">→</span> {route.dropoff_label}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {route.miles} miles · {route.km} km driving distance
              {route.crosses_channel && <span className="ml-2 text-accent font-medium">+ Eurotunnel crossing</span>}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="quote-reset-btn">New quote</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {prices.map((v) => (
            <div
              key={v.key}
              className="rounded-lg border border-border bg-card p-5 flex flex-col"
              data-testid={`quote-price-${v.key}`}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">{v.blurb}</p>
              <h3 className="font-display text-lg mt-2">{v.label}</h3>
              <p className="mt-4">
                <span className="font-display text-4xl font-medium">£{v.total}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">Total door-to-door</p>
              <Button asChild className="mt-5 w-full" data-testid={`quote-book-${v.key}`}>
                <Link to={bookingUrl(v.key)}>
                  Book this vehicle <ArrowRight size={14} className="ml-2" />
                </Link>
              </Button>
            </div>
          ))}
        </div>

        {route.crosses_channel && (
          <div className="rounded-md bg-accent/10 border border-accent/40 p-4 flex items-start gap-3" data-testid="quote-eurotunnel-notice">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground shrink-0">+ Eurotunnel</span>
            <div className="text-sm">
              <p className="font-medium text-foreground">Channel crossing required</p>
              <p className="text-muted-foreground text-xs mt-1">
                Eurotunnel LeShuttle Pet crossing is booked and invoiced separately by our team (typically £130–£250 one-way).
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Initial two-field instant-quote form
  return (
    <form onSubmit={calculate} className="space-y-5" data-testid="quick-quote-form">
      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup location</Label>
          <AddressAutocomplete
            value={pickup}
            onChange={setPickup}
            placeholder="London, UK"
            required
            testId="quote-pickup"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Drop-off location</Label>
          <AddressAutocomplete
            value={dropoff}
            onChange={setDropoff}
            placeholder="Paris, France"
            required
            testId="quote-dropoff"
          />
        </div>
      </div>

      <Button type="submit" disabled={loading} className="w-full h-12 text-base" data-testid="quote-calculate-btn">
        {loading ? (
          <>
            <Loader2 size={16} className="mr-2 animate-spin" /> Calculating distance…
          </>
        ) : (
          <>
            <Calculator size={16} className="mr-2" /> Get instant price
          </>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-2">
        <MapPin size={12} /> We calculate the exact driving distance automatically — no guesswork.
      </p>
    </form>
  );
}
